import { useCallback, useEffect, useState } from "react";
import EmptyState from "../components/EmptyState";
import ErrorAlert from "../components/ErrorAlert";
import LoadingState from "../components/LoadingState";
import PageTitle from "../components/PageTitle";
import { adminApi } from "../services/adminApi";
import { getResults } from "../utils/formatters";

function StatusLabel({ active, label }) {
  return <span className={active ? "badge badge-success" : "badge badge-muted"}>{label}</span>;
}

function LocationsPage({ onNavigate }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = await adminApi("/locations/");
      setLocations(getResults(payload));
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  return (
    <main className="admin-page flex flex-column gap-5">
      <PageTitle eyebrow="Locations" title="Location Management">
        Create locations, edit service availability, and deactivate locations safely.
      </PageTitle>

      <section className="card admin-panel">
        <header className="card-header admin-section-header">
          <h2>Locations</h2>
          <button className="btn btn-primary" onClick={() => onNavigate("/admin/locations/new")} type="button">
            New Location
          </button>
        </header>
        <div className="card-body">
          {error ? <ErrorAlert error={error} onRetry={loadLocations} /> : null}
          {loading ? <LoadingState /> : null}
          {!loading && !error && !locations.length ? (
            <EmptyState
              text="No locations yet. Create your first location so customers can choose where to order."
              action={<button className="btn btn-primary" onClick={() => onNavigate("/admin/locations/new")} type="button">Create Location</button>}
            />
          ) : null}
          {!loading && !error && locations.length ? (
            <div className="admin-list admin-click-list">
              {locations.map((location) => (
                <article
                  key={location.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onNavigate(`/admin/locations/${location.id}/edit`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      onNavigate(`/admin/locations/${location.id}/edit`);
                    }
                  }}
                >
                  <strong>{location.name}</strong>
                  <span>{location.city}, {location.state}</span>
                  <span className="flex gap-2 flex-wrap">
                    <StatusLabel active={location.accepts_pickup} label="Pickup" />
                    <StatusLabel active={location.accepts_delivery} label="Delivery" />
                    <StatusLabel active={location.accepts_catering} label="Catering" />
                    <StatusLabel active={location.is_active} label={location.is_active ? "Active" : "Inactive"} />
                  </span>
                  <button className="btn btn-sm btn-secondary" type="button">
                    Edit
                  </button>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}

export default LocationsPage;